import React from 'react';
import { Check, X, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { isAdminUser, canModifyRecord, approvalBadge } from '../utils/helpers';

/**
 * ApprovalBadge — shows an approval status pill, plus optional creator name.
 */
export function ApprovalBadge({ status, createdByName }) {
  const b = approvalBadge(status);
  return (
    <>
      <span style={{background:b.bg,color:b.color,padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,display:'inline-block'}}>
        {b.label}
      </span>
      {createdByName && (
        <small style={{display:'block',color:'#64748b',fontSize:10,marginTop:2}}>by {createdByName}</small>
      )}
    </>
  );
}

/**
 * RowActions — admin sees Approve/Reject for pending + Edit/Delete.
 * Staff sees Edit/Delete only on their own pending records.
 * - endpoint: backend route prefix like '/ledgers' or '/products'
 * - row: the data row (must have id, approval_status, created_by)
 * - onChange: callback to reload list after action
 * - onEdit: function to open edit modal
 * - confirmDeleteMsg: confirmation text shown for delete
 * - confirmApproveMsg / confirmRejectMsg: optional override
 */
export function RowActions({ endpoint, row, onChange, onEdit, confirmDeleteMsg = 'Delete this record?', confirmApproveMsg, confirmRejectMsg }) {
  const admin = isAdminUser();
  const isPending = row.approval_status === 'pending';
  const canMod = canModifyRecord(row);

  const approve = async () => {
    if (!window.confirm(confirmApproveMsg || 'Approve this record? Staff will no longer be able to edit it.')) return;
    try { await api.post(`${endpoint}/${row.id}`); toast.success('Approved'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const reject = async () => {
    if (!window.confirm(confirmRejectMsg || 'Reject this record? It will be removed permanently.')) return;
    try { await api.post(`${endpoint}/${row.id}/reject`); toast.success('Rejected'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const del = async () => {
    if (!window.confirm(confirmDeleteMsg)) return;
    try { await api.delete(`${endpoint}/${row.id}`); toast.success('Deleted'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  return (
    <>
      {admin && isPending && (
        <>
          <button className="btn btn-sm" style={{background:'#10b981',color:'white'}} onClick={approve} title="Approve"><Check size={12}/></button>
          {' '}
          <button className="btn btn-sm" style={{background:'#ef4444',color:'white'}} onClick={reject} title="Reject"><X size={12}/></button>
          {' '}
        </>
      )}
      {canMod && onEdit && (
        <>
          <button className="btn btn-sm btn-gray" onClick={()=>onEdit(row)} title="Edit"><Edit2 size={12}/></button>
          {' '}
        </>
      )}
      {canMod && (
        <button className="btn btn-sm btn-red" onClick={del} title="Delete"><Trash2 size={12}/></button>
      )}
      {!canMod && !isPending && !admin && (
        <small style={{color:'#94a3b8',fontSize:11}}>locked</small>
      )}
    </>
  );
}

/**
 * PendingApprovalBanner — yellow info banner shown in create modal for staff users.
 */
export function PendingApprovalBanner({ isEdit }) {
  const admin = isAdminUser();
  if (admin || isEdit) return null;
  return (
    <div style={{padding:'10px 14px',background:'#fef3c7',color:'#92400e',borderRadius:8,marginBottom:12,fontSize:13}}>
      ⓘ This record will be submitted for admin approval. It will become active once approved.
    </div>
  );
}

/**
 * PendingCountBadge — shown next to page title, indicates how many pending records.
 */
export function PendingCountBadge({ count }) {
  if (!count) return null;
  return (
    <span style={{marginLeft:10,padding:'2px 10px',background:'#fef3c7',color:'#92400e',borderRadius:12,fontSize:12,fontWeight:600}}>
      {count} pending
    </span>
  );
}
